import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Package, Warehouse, ShoppingCart, TrendingUp,
  Clock, ChevronRight,
  Check, Mail, MapPin, Menu, X,
  CheckCircle, FileText, Globe, Box, Truck,
  RotateCcw, Calculator, ArrowRight
} from 'lucide-react';

const LandingPageGoogleAds = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    volume: ''
  });
  const [leadMagnetData, setLeadMagnetData] = useState({
    name: '',
    email: '',
    volume: ''
  });
  const [estimatorData, setEstimatorData] = useState({
    orders: '',
    products: '',
    destination: ''
  });
  const [showEstimate, setShowEstimate] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [leadMagnetStatus, setLeadMagnetStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/contact', formData);
      setFormStatus({ type: 'success', message: t('landing.contact.form.success') });
      setFormData({ name: '', company: '', email: '', phone: '', message: '', volume: '' });

      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17995039254/FCllCMex4YMcEJaE2oRD',
          'value': 1.0,
          'currency': 'EUR'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.response?.data?.detail || t('landing.contact.form.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeadMagnetSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/lead-magnet', leadMagnetData);

      setLeadMagnetStatus({
        type: 'success',
        message: t('landing.leadMagnet.success')
      });
      setLeadMagnetData({ name: '', email: '', volume: '' });

      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17995039254/FCllCMex4YMcEJaE2oRD',
          'value': 0.5,
          'currency': 'EUR'
        });
      }
    } catch (error) {
      setLeadMagnetStatus({
        type: 'error',
        message: error.response?.data?.detail || t('landing.leadMagnet.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstimatorSubmit = (e) => {
    e.preventDefault();
    setShowEstimate(true);
    if (window.gtag) {
      window.gtag('event', 'cost_estimator_used', {
        'orders': estimatorData.orders,
        'products': estimatorData.products,
        'destination': estimatorData.destination
      });
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToEstimator = () => {
    document.getElementById('cost-estimator')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>{t('landing.meta.title')}</title>
        <meta name="description" content={t('landing.meta.description')} />
        <link rel="canonical" href="https://www.newstaq.com/" />
      </Helmet>

      {/* Sticky CTA Bar */}
      {showStickyBar && (
        <div style={styles.stickyBar}>
          <div style={styles.stickyBarContent}>
            <span style={styles.stickyBarText}>{t('landing.stickyBar.text')}</span>
            <button onClick={scrollToForm} style={styles.stickyBarButton}>
              {t('landing.stickyBar.cta')} <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logo}>
            <Package size={isMobile ? 28 : 32} color="#3b82f6" />
            <span style={styles.logoText}>NEWSTAQ</span>
          </div>
          {isMobile && (
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} style={styles.hamburger}>
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
          {!isMobile && (
            <nav style={styles.nav}>
              <a href="#services" style={styles.navLink}>{t('landing.nav.services')}</a>
              <a href="#pour-qui" style={styles.navLink}>{t('landing.nav.pourQui')}</a>
              <a href="#comment" style={styles.navLink}>{t('landing.nav.comment')}</a>
              <a href="#tarifs" style={styles.navLink}>{t('landing.nav.tarifs')}</a>
              <a href="#faq" style={styles.navLink}>FAQ</a>
              <LanguageSwitcher />
              <button onClick={scrollToForm} style={styles.headerCtaButton}>
                {t('landing.nav.devis')}
              </button>
            </nav>
          )}
        </div>
        {isMobile && isMobileMenuOpen && (
          <nav style={styles.mobileMenu}>
            <a href="#services" style={styles.mobileNavLink} onClick={() => setIsMobileMenuOpen(false)}>{t('landing.nav.services')}</a>
            <a href="#pour-qui" style={styles.mobileNavLink} onClick={() => setIsMobileMenuOpen(false)}>{t('landing.nav.pourQui')}</a>
            <a href="#comment" style={styles.mobileNavLink} onClick={() => setIsMobileMenuOpen(false)}>{t('landing.nav.comment')}</a>
            <a href="#tarifs" style={styles.mobileNavLink} onClick={() => setIsMobileMenuOpen(false)}>{t('landing.nav.tarifs')}</a>
            <a href="#faq" style={styles.mobileNavLink} onClick={() => setIsMobileMenuOpen(false)}>FAQ</a>
            <LanguageSwitcher isMobile={true} />
            <button onClick={() => { scrollToForm(); setIsMobileMenuOpen(false); }} style={styles.mobileLoginButton}>
              {t('landing.nav.devis')}
            </button>
          </nav>
        )}
      </header>

      {/* ============================================ */}
      {/* 1 — HERO SECTION                            */}
      {/* ============================================ */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.heroLeft}>
            <h1 style={styles.heroTitle}>
              {t('landing.hero.title')}
            </h1>
            <p style={styles.heroSubtitle}>
              {t('landing.hero.subtitle')}
            </p>

            <div style={styles.heroBullets}>
              <div style={styles.heroBullet}>
                <Check size={20} color="#10b981" style={{flexShrink: 0}} />
                <span>{t('landing.hero.bullet1')}</span>
              </div>
              <div style={styles.heroBullet}>
                <Check size={20} color="#10b981" style={{flexShrink: 0}} />
                <span>{t('landing.hero.bullet2')}</span>
              </div>
              <div style={styles.heroBullet}>
                <Check size={20} color="#10b981" style={{flexShrink: 0}} />
                <span>{t('landing.hero.bullet3')}</span>
              </div>
            </div>

            <div style={styles.heroButtons}>
              <button onClick={scrollToForm} style={styles.ctaPrimary}>
                {t('landing.hero.ctaPrimary')}
                <ArrowRight size={20} />
              </button>
              <button onClick={scrollToEstimator} style={styles.ctaSecondary}>
                <Calculator size={20} />
                {t('landing.hero.ctaSecondary')}
              </button>
            </div>

            <p style={styles.heroCredibility}>
              {t('landing.hero.credibility')}
            </p>
          </div>

          <div style={styles.heroRight}>
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80"
              alt="Entrepot logistique moderne NEWSTAQ region parisienne"
              style={styles.heroImage}
            />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 2 — TRUST SECTION                           */}
      {/* ============================================ */}
      <section style={styles.trustSection}>
        <div style={styles.sectionContent}>
          <div style={styles.trustGrid}>
            <div style={styles.trustCard}>
              <div style={styles.trustNumber}>2 000 m&sup2;</div>
              <div style={styles.trustLabel}>{t('landing.trust.warehouse')}</div>
            </div>
            <div style={styles.trustCard}>
              <div style={styles.trustNumber}>J+0</div>
              <div style={styles.trustLabel}>{t('landing.trust.shipping')}</div>
            </div>
            <div style={styles.trustCard}>
              <div style={styles.trustNumber}>25+</div>
              <div style={styles.trustLabel}>{t('landing.trust.integrations')}</div>
            </div>
            <div style={styles.trustCard}>
              <div style={styles.trustNumber}>EU + CN</div>
              <div style={styles.trustLabel}>{t('landing.trust.crossborder')}</div>
            </div>
          </div>

          {/* Client logos placeholder */}
          <div style={styles.clientLogos}>
            <p style={styles.clientLogosText}>{t('landing.trust.clientsTitle')}</p>
            <div style={styles.clientLogosGrid}>
              {['Shopify', 'Amazon', 'WooCommerce', 'Chronopost', 'DHL', 'Colissimo'].map((name) => (
                <div key={name} style={styles.clientLogoPlaceholder}>
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 3 — HOW IT WORKS (4 steps)                  */}
      {/* ============================================ */}
      <section id="comment" style={styles.commentSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.comment.title')}</h2>
          <p style={styles.sectionSubtitle}>{t('landing.comment.subtitle')}</p>

          <div style={styles.processGrid}>
            <div style={styles.processStep}>
              <div style={styles.processNumber}>1</div>
              <div style={styles.processIcon}>
                <Truck size={32} color="#3b82f6" />
              </div>
              <h3 style={styles.processTitle}>{t('landing.comment.step1.title')}</h3>
              <p style={styles.processText}>{t('landing.comment.step1.text')}</p>
            </div>

            <div style={styles.processStep}>
              <div style={styles.processNumber}>2</div>
              <div style={styles.processIcon}>
                <Warehouse size={32} color="#3b82f6" />
              </div>
              <h3 style={styles.processTitle}>{t('landing.comment.step2.title')}</h3>
              <p style={styles.processText}>{t('landing.comment.step2.text')}</p>
            </div>

            <div style={styles.processStep}>
              <div style={styles.processNumber}>3</div>
              <div style={styles.processIcon}>
                <Package size={32} color="#3b82f6" />
              </div>
              <h3 style={styles.processTitle}>{t('landing.comment.step3.title')}</h3>
              <p style={styles.processText}>{t('landing.comment.step3.text')}</p>
            </div>

            <div style={styles.processStep}>
              <div style={styles.processNumber}>4</div>
              <div style={styles.processIcon}>
                <RotateCcw size={32} color="#10b981" />
              </div>
              <h3 style={styles.processTitle}>{t('landing.comment.step4.title')}</h3>
              <p style={styles.processText}>{t('landing.comment.step4.text')}</p>
            </div>
          </div>

          <div style={{textAlign: 'center', marginTop: '3rem'}}>
            <button onClick={scrollToForm} style={styles.ctaPrimary}>
              {t('landing.comment.cta')}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 4 — SERVICES SECTION                        */}
      {/* ============================================ */}
      <section id="services" style={styles.servicesSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.services.title')}</h2>
          <p style={styles.sectionSubtitle}>{t('landing.services.subtitle')}</p>

          <div style={styles.servicesGrid}>
            <div style={styles.serviceCard}>
              <div style={{...styles.serviceIcon, backgroundColor: '#dbeafe'}}>
                <Warehouse size={36} color="#3b82f6" />
              </div>
              <h3 style={styles.serviceCardTitle}>{t('landing.services.storage.title')}</h3>
              <p style={styles.serviceCardText}>{t('landing.services.storage.text')}</p>
              <ul style={styles.serviceList}>
                <li style={styles.serviceListItem}><Check size={16} color="#3b82f6" /><span>{t('landing.services.storage.item1')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#3b82f6" /><span>{t('landing.services.storage.item2')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#3b82f6" /><span>{t('landing.services.storage.item3')}</span></li>
              </ul>
            </div>

            <div style={styles.serviceCard}>
              <div style={{...styles.serviceIcon, backgroundColor: '#d1fae5'}}>
                <Package size={36} color="#10b981" />
              </div>
              <h3 style={styles.serviceCardTitle}>{t('landing.services.fulfillment.title')}</h3>
              <p style={styles.serviceCardText}>{t('landing.services.fulfillment.text')}</p>
              <ul style={styles.serviceList}>
                <li style={styles.serviceListItem}><Check size={16} color="#10b981" /><span>{t('landing.services.fulfillment.item1')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#10b981" /><span>{t('landing.services.fulfillment.item2')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#10b981" /><span>{t('landing.services.fulfillment.item3')}</span></li>
              </ul>
            </div>

            <div style={styles.serviceCard}>
              <div style={{...styles.serviceIcon, backgroundColor: '#fef3c7'}}>
                <Truck size={36} color="#f59e0b" />
              </div>
              <h3 style={styles.serviceCardTitle}>{t('landing.services.shipping.title')}</h3>
              <p style={styles.serviceCardText}>{t('landing.services.shipping.text')}</p>
              <ul style={styles.serviceList}>
                <li style={styles.serviceListItem}><Check size={16} color="#f59e0b" /><span>{t('landing.services.shipping.item1')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#f59e0b" /><span>{t('landing.services.shipping.item2')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#f59e0b" /><span>{t('landing.services.shipping.item3')}</span></li>
              </ul>
            </div>

            <div style={styles.serviceCard}>
              <div style={{...styles.serviceIcon, backgroundColor: '#fce7f3'}}>
                <RotateCcw size={36} color="#ec4899" />
              </div>
              <h3 style={styles.serviceCardTitle}>{t('landing.services.returns.title')}</h3>
              <p style={styles.serviceCardText}>{t('landing.services.returns.text')}</p>
              <ul style={styles.serviceList}>
                <li style={styles.serviceListItem}><Check size={16} color="#ec4899" /><span>{t('landing.services.returns.item1')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#ec4899" /><span>{t('landing.services.returns.item2')}</span></li>
                <li style={styles.serviceListItem}><Check size={16} color="#ec4899" /><span>{t('landing.services.returns.item3')}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 5 — WHO WE WORK WITH                        */}
      {/* ============================================ */}
      <section id="pour-qui" style={styles.idealClientsSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.idealClients.title')}</h2>
          <p style={styles.sectionSubtitle}>{t('landing.idealClients.subtitle')}</p>

          <div style={styles.idealClientsGrid}>
            <div style={styles.idealClientCard}>
              <ShoppingCart size={40} color="#3b82f6" />
              <h3 style={styles.idealClientTitle}>{t('landing.idealClients.shopify.title')}</h3>
              <p style={styles.idealClientText}>{t('landing.idealClients.shopify.text')}</p>
            </div>
            <div style={styles.idealClientCard}>
              <TrendingUp size={40} color="#10b981" />
              <h3 style={styles.idealClientTitle}>{t('landing.idealClients.growing.title')}</h3>
              <p style={styles.idealClientText}>{t('landing.idealClients.growing.text')}</p>
            </div>
            <div style={styles.idealClientCard}>
              <Globe size={40} color="#f59e0b" />
              <h3 style={styles.idealClientTitle}>{t('landing.idealClients.international.title')}</h3>
              <p style={styles.idealClientText}>{t('landing.idealClients.international.text')}</p>
            </div>
            <div style={styles.idealClientCard}>
              <Box size={40} color="#ec4899" />
              <h3 style={styles.idealClientTitle}>{t('landing.idealClients.chinese.title')}</h3>
              <p style={styles.idealClientText}>{t('landing.idealClients.chinese.text')}</p>
            </div>
          </div>

          <p style={styles.idealClientsFooter}>{t('landing.idealClients.footer')}</p>
        </div>
      </section>

      {/* ============================================ */}
      {/* Integrations                                */}
      {/* ============================================ */}
      <section style={styles.integrationsSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.integrations.title')}</h2>
          <p style={styles.sectionSubtitle}>{t('landing.integrations.subtitle')}</p>

          <div style={styles.integrationsContainer}>
            <div style={styles.integrationsBlock}>
              <h3 style={styles.integrationsBlockTitle}>{t('landing.integrations.ecommerce')}</h3>
              <div style={styles.integrationsGrid}>
                <div style={styles.integrationTag}>Shopify</div>
                <div style={styles.integrationTag}>WooCommerce</div>
                <div style={styles.integrationTag}>PrestaShop</div>
                <div style={styles.integrationTag}>Amazon FBA</div>
                <div style={styles.integrationTag}>Cdiscount</div>
                <div style={styles.integrationTag}>TikTok Shop</div>
                <div style={styles.integrationTag}>Zalando</div>
              </div>
            </div>

            <div style={styles.integrationsBlock}>
              <h3 style={styles.integrationsBlockTitle}>{t('landing.integrations.transporteurs')}</h3>
              <div style={styles.integrationsGrid}>
                <div style={styles.integrationTag}>Colissimo</div>
                <div style={styles.integrationTag}>Chronopost</div>
                <div style={styles.integrationTag}>DHL</div>
                <div style={styles.integrationTag}>UPS</div>
                <div style={styles.integrationTag}>Mondial Relay</div>
                <div style={styles.integrationTag}>GLS</div>
              </div>
              <p style={styles.integrationsMention}>{t('landing.integrations.mention')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 6 — COST ESTIMATOR                          */}
      {/* ============================================ */}
      <section id="cost-estimator" style={styles.estimatorSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.estimator.title')}</h2>
          <p style={styles.sectionSubtitle}>{t('landing.estimator.subtitle')}</p>

          <div style={styles.estimatorCard}>
            <form onSubmit={handleEstimatorSubmit} style={styles.estimatorForm}>
              <div style={styles.estimatorFields}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.estimator.orders')}</label>
                  <select
                    required
                    value={estimatorData.orders}
                    onChange={(e) => setEstimatorData({...estimatorData, orders: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">{t('landing.estimator.selectOrders')}</option>
                    <option value="<100">{'< 100'}</option>
                    <option value="100-500">100 - 500</option>
                    <option value="500-1000">500 - 1 000</option>
                    <option value="1000-5000">1 000 - 5 000</option>
                    <option value="5000+">5 000+</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.estimator.products')}</label>
                  <select
                    required
                    value={estimatorData.products}
                    onChange={(e) => setEstimatorData({...estimatorData, products: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">{t('landing.estimator.selectProducts')}</option>
                    <option value="1">1</option>
                    <option value="2-3">2 - 3</option>
                    <option value="4-5">4 - 5</option>
                    <option value="5+">5+</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.estimator.destination')}</label>
                  <select
                    required
                    value={estimatorData.destination}
                    onChange={(e) => setEstimatorData({...estimatorData, destination: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">{t('landing.estimator.selectDestination')}</option>
                    <option value="france">{t('landing.estimator.france')}</option>
                    <option value="europe">{t('landing.estimator.europe')}</option>
                    <option value="worldwide">{t('landing.estimator.worldwide')}</option>
                  </select>
                </div>
              </div>

              <button type="submit" style={styles.estimatorButton}>
                <Calculator size={20} />
                {t('landing.estimator.cta')}
              </button>
            </form>

            {showEstimate && (
              <div style={styles.estimatorResult}>
                <CheckCircle size={24} color="#10b981" />
                <div>
                  <p style={styles.estimatorResultTitle}>{t('landing.estimator.resultTitle')}</p>
                  <p style={styles.estimatorResultText}>{t('landing.estimator.resultText')}</p>
                  <button onClick={scrollToForm} style={styles.estimatorResultCta}>
                    {t('landing.estimator.resultCta')} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* Lead Magnet - Grille Tarifaire               */}
      {/* ============================================ */}
      <section id="tarifs" style={styles.leadMagnetSection}>
        <div style={styles.sectionContent}>
          <div style={styles.leadMagnetContainer}>
            <div style={styles.leadMagnetLeft}>
              <h2 style={styles.leadMagnetTitle}>{t('landing.leadMagnet.title')}</h2>
              <p style={styles.leadMagnetSubtitle}>{t('landing.leadMagnet.subtitle')}</p>

              <ul style={styles.leadMagnetList}>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <li key={num} style={styles.leadMagnetListItem}>
                    <Check size={18} color="#10b981" />
                    <span>{t(`landing.leadMagnet.item${num}`)}</span>
                  </li>
                ))}
              </ul>

              <div style={styles.leadMagnetImage}>
                <FileText size={80} color="#3b82f6" />
                <p style={styles.leadMagnetImageText}>{t('landing.leadMagnet.pdfTitle')}</p>
              </div>
            </div>

            <div style={styles.leadMagnetRight}>
              <form onSubmit={handleLeadMagnetSubmit} style={styles.leadMagnetForm}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.leadMagnet.form.name')} *</label>
                  <input
                    type="text"
                    required
                    value={leadMagnetData.name}
                    onChange={(e) => setLeadMagnetData({...leadMagnetData, name: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.leadMagnet.form.namePlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.leadMagnet.form.email')} *</label>
                  <input
                    type="email"
                    required
                    value={leadMagnetData.email}
                    onChange={(e) => setLeadMagnetData({...leadMagnetData, email: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.leadMagnet.form.emailPlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.leadMagnet.form.volume')}</label>
                  <select
                    value={leadMagnetData.volume}
                    onChange={(e) => setLeadMagnetData({...leadMagnetData, volume: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">{t('landing.leadMagnet.form.volumePlaceholder')}</option>
                    <option value="<50">{t('landing.leadMagnet.form.volume1')}</option>
                    <option value="50-200">{t('landing.leadMagnet.form.volume2')}</option>
                    <option value="200-500">{t('landing.leadMagnet.form.volume3')}</option>
                    <option value="500-1000">{t('landing.leadMagnet.form.volume4')}</option>
                    <option value="1000+">{t('landing.leadMagnet.form.volume5')}</option>
                    <option value="grossiste">{t('landing.leadMagnet.form.volume6')}</option>
                  </select>
                </div>

                <button
                  type="submit"
                  style={{
                    ...styles.submitButton,
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('landing.leadMagnet.form.submitting') : t('landing.leadMagnet.form.submit')}
                  {!isSubmitting && <FileText size={20} />}
                </button>

                {leadMagnetStatus.message && (
                  <div style={{
                    ...styles.formMessage,
                    backgroundColor: leadMagnetStatus.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: leadMagnetStatus.type === 'success' ? '#065f46' : '#991b1b'
                  }}>
                    {leadMagnetStatus.message}
                  </div>
                )}

                <p style={styles.rgpdText}>{t('landing.leadMagnet.form.rgpd')}</p>
                <p style={styles.trustBadge}>{t('landing.leadMagnet.form.trust')}</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FAQ                                          */}
      {/* ============================================ */}
      <section id="faq" style={styles.faqSection}>
        <div style={styles.sectionContent}>
          <h2 style={styles.sectionTitle}>{t('landing.faq.title')}</h2>

          <div style={styles.faqGrid}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
              <div key={num} style={styles.faqItem}>
                <button
                  onClick={() => setOpenFaq(openFaq === num ? null : num)}
                  style={styles.faqQuestionButton}
                >
                  <h3 style={styles.faqQuestion}>{t(`landing.faq.q${num}.question`)}</h3>
                  <ChevronRight
                    size={20}
                    style={{
                      transform: openFaq === num ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                      color: '#3b82f6'
                    }}
                  />
                </button>
                {openFaq === num && (
                  <div style={styles.faqAnswer} dangerouslySetInnerHTML={{__html: t(`landing.faq.q${num}.answer`)}} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 7 — LEAD FORM                               */}
      {/* ============================================ */}
      <section id="contact-form" style={styles.contactSection}>
        <div style={styles.sectionContent}>
          <div style={styles.contactHeader}>
            <h2 style={styles.sectionTitleWhite}>{t('landing.contact.title')}</h2>
            <p style={styles.sectionSubtitleWhite}>{t('landing.contact.subtitle')}</p>
          </div>

          <div style={styles.contactContainer}>
            <form onSubmit={handleSubmit} style={styles.contactForm}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.name')} *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.contact.form.namePlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.company')} *</label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.contact.form.companyPlaceholder')}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.email')} *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.contact.form.emailPlaceholder')}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.contact.form.phonePlaceholder')}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.volume')}</label>
                  <select
                    value={formData.volume}
                    onChange={(e) => setFormData({...formData, volume: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">{t('landing.contact.form.volumePlaceholder')}</option>
                    <option value="<50">{t('landing.contact.form.volume1')}</option>
                    <option value="50-200">{t('landing.contact.form.volume2')}</option>
                    <option value="200-500">{t('landing.contact.form.volume3')}</option>
                    <option value="500-1000">{t('landing.contact.form.volume4')}</option>
                    <option value="1000+">{t('landing.contact.form.volume5')}</option>
                    <option value="grossiste">{t('landing.contact.form.volume6')}</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('landing.contact.form.country')}</label>
                  <input
                    type="text"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    style={styles.input}
                    placeholder={t('landing.contact.form.countryPlaceholder')}
                  />
                </div>
              </div>

              <button
                type="submit"
                style={{
                  ...styles.submitButtonLarge,
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('landing.contact.form.submitting') : t('landing.contact.form.submit')}
                {!isSubmitting && <ArrowRight size={20} />}
              </button>

              {formStatus.message && (
                <div style={{
                  ...styles.formMessage,
                  backgroundColor: formStatus.type === 'success' ? '#d1fae5' : '#fee2e2',
                  color: formStatus.type === 'success' ? '#065f46' : '#991b1b'
                }}>
                  {formStatus.message}
                </div>
              )}

              <div style={styles.contactTrust}>
                <span><Check size={16} color="#10b981" /> {t('landing.contact.trust.item1')}</span>
                <span><Check size={16} color="#10b981" /> {t('landing.contact.trust.item2')}</span>
                <span><Check size={16} color="#10b981" /> {t('landing.contact.trust.item3')}</span>
              </div>
            </form>

            <div style={styles.contactInfo}>
              <h3 style={styles.contactInfoTitle}>{t('landing.contact.info.title')}</h3>

              <div style={styles.contactInfoItem}>
                <Mail size={24} color="#ffffff" />
                <div>
                  <div style={styles.contactInfoLabel}>{t('landing.contact.info.email')}</div>
                  <a href="mailto:contact@newstaq.com" style={styles.contactInfoValue}>
                    contact@newstaq.com
                  </a>
                </div>
              </div>

              <div style={styles.contactInfoItem}>
                <MapPin size={24} color="#ffffff" />
                <div>
                  <div style={styles.contactInfoLabel}>{t('landing.contact.info.address')}</div>
                  <div style={styles.contactInfoValue}>
                    1-3 rue d'Ableval<br />
                    95200 Sarcelles<br />
                    Ile-de-France, FRANCE
                  </div>
                </div>
              </div>

              <div style={styles.contactInfoItem}>
                <Clock size={24} color="#ffffff" />
                <div>
                  <div style={styles.contactInfoLabel}>{t('landing.contact.info.hours')}</div>
                  <div style={styles.contactInfoValue}>
                    {t('landing.contact.info.hoursValue')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 8 — FINAL CTA                               */}
      {/* ============================================ */}
      <section style={styles.finalCtaSection}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>{t('landing.finalCta.title')}</h2>
          <p style={styles.finalCtaSubtitle}>{t('landing.finalCta.subtitle')}</p>
          <button onClick={scrollToForm} style={styles.finalCtaButton}>
            {t('landing.finalCta.cta')}
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerGrid}>
            <div style={styles.footerColumn}>
              <div style={styles.footerLogo}>
                <Package size={24} color="#3b82f6" />
                <span style={styles.footerLogoText}>NEWSTAQ</span>
              </div>
              <p style={styles.footerDescription}>
                {t('landing.footer.description')}
              </p>
            </div>

            <div style={styles.footerColumn}>
              <h4 style={styles.footerColumnTitle}>{t('landing.footer.services.title')}</h4>
              <ul style={styles.footerList}>
                <li><a href="#services" style={styles.footerLink}>{t('landing.footer.services.item1')}</a></li>
                <li><a href="#services" style={styles.footerLink}>{t('landing.footer.services.item2')}</a></li>
                <li><a href="#services" style={styles.footerLink}>{t('landing.footer.services.item3')}</a></li>
                <li><a href="#services" style={styles.footerLink}>{t('landing.footer.services.item4')}</a></li>
                <li><a href="#services" style={styles.footerLink}>{t('landing.footer.services.item5')}</a></li>
              </ul>
            </div>

            <div style={styles.footerColumn}>
              <h4 style={styles.footerColumnTitle}>{t('landing.footer.resources.title')}</h4>
              <ul style={styles.footerList}>
                <li><a href="#tarifs" style={styles.footerLink}>{t('landing.footer.resources.item1')}</a></li>
                <li><a href="#faq" style={styles.footerLink}>{t('landing.footer.resources.item2')}</a></li>
                <li><a href="#contact-form" style={styles.footerLink}>{t('landing.footer.resources.item3')}</a></li>
              </ul>
            </div>

            <div style={styles.footerColumn}>
              <h4 style={styles.footerColumnTitle}>{t('landing.footer.contact.title')}</h4>
              <p style={styles.footerContactItem}>
                <Mail size={16} style={{marginRight: '0.5rem'}} />
                contact@newstaq.com
              </p>
              <p style={styles.footerContactItem}>
                <MapPin size={16} style={{marginRight: '0.5rem'}} />
                Sarcelles, Ile-de-France
              </p>
            </div>
          </div>

          <div style={styles.footerBottom}>
            <p style={styles.footerCopyright}>
              &copy; 2026 NEWSTAQ. {t('landing.footer.copyright')}
            </p>
            <div style={styles.footerLinks}>
              <a href="/mentions-legales" style={styles.footerLegalLink}>{t('landing.footer.legal.item1')}</a>
              <a href="/cgv" style={styles.footerLegalLink}>{t('landing.footer.legal.item2')}</a>
              <a href="/confidentialite" style={styles.footerLegalLink}>{t('landing.footer.legal.item3')}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==========================================
// STYLES
// ==========================================
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  // Sticky Bar
  stickyBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '12px 0',
    zIndex: 999,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  stickyBarContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  stickyBarText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  stickyBarButton: {
    backgroundColor: 'white',
    color: '#3b82f6',
    border: 'none',
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'transform 0.2s',
  },

  // Header
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '1rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    cursor: 'pointer',
  },
  logoText: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#0f172a',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.75rem',
  },
  navLink: {
    color: '#475569',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'color 0.2s',
  },
  headerCtaButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  hamburger: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenu: {
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  mobileNavLink: {
    color: '#475569',
    textDecoration: 'none',
    fontSize: '1rem',
    padding: '0.5rem 0',
  },
  mobileLoginButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },

  // Hero
  hero: {
    backgroundColor: '#f8fafc',
    padding: '5rem 0 4rem',
  },
  heroContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '3rem',
    alignItems: 'center',
  },
  heroLeft: {
    maxWidth: '600px',
  },
  heroTitle: {
    fontSize: '2.75rem',
    fontWeight: '800',
    color: '#0f172a',
    lineHeight: 1.15,
    marginBottom: '1.25rem',
  },
  heroSubtitle: {
    fontSize: '1.2rem',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: '2rem',
  },
  heroBullets: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '2rem',
  },
  heroBullet: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    fontSize: '1rem',
    color: '#334155',
  },
  heroButtons: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  ctaPrimary: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
  },
  ctaSecondary: {
    backgroundColor: 'transparent',
    color: '#3b82f6',
    border: '2px solid #3b82f6',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  heroCredibility: {
    fontSize: '0.9rem',
    color: '#64748b',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  heroRight: {
    display: 'flex',
    justifyContent: 'center',
  },
  heroImage: {
    width: '100%',
    maxWidth: '500px',
    borderRadius: '1rem',
    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
  },

  // Trust Section
  trustSection: {
    padding: '3rem 0',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #f1f5f9',
  },
  trustGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  trustCard: {
    textAlign: 'center',
    padding: '1.5rem 1rem',
  },
  trustNumber: {
    fontSize: '2.25rem',
    fontWeight: '800',
    color: '#3b82f6',
    marginBottom: '0.25rem',
  },
  trustLabel: {
    fontSize: '0.9rem',
    color: '#64748b',
    fontWeight: '500',
  },
  clientLogos: {
    textAlign: 'center',
    padding: '2rem 0 0',
    borderTop: '1px solid #f1f5f9',
  },
  clientLogosText: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: '600',
    marginBottom: '1.5rem',
  },
  clientLogosGrid: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '1.5rem',
  },
  clientLogoPlaceholder: {
    backgroundColor: '#f8fafc',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    color: '#94a3b8',
    fontWeight: '600',
    border: '1px solid #e2e8f0',
  },

  // Section Common
  sectionContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#0f172a',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  sectionSubtitle: {
    fontSize: '1.125rem',
    color: '#64748b',
    textAlign: 'center',
    marginBottom: '3rem',
    maxWidth: '700px',
    margin: '0 auto 3rem',
  },

  // How It Works
  commentSection: {
    padding: '5rem 0',
    backgroundColor: '#f8fafc',
  },
  processGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '2rem',
    marginTop: '2rem',
  },
  processStep: {
    position: 'relative',
    textAlign: 'center',
    padding: '2.5rem 1.5rem 2rem',
    backgroundColor: '#ffffff',
    borderRadius: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  processNumber: {
    position: 'absolute',
    top: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '36px',
    height: '36px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  processIcon: {
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
  processTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: '0.75rem',
  },
  processText: {
    fontSize: '0.95rem',
    color: '#475569',
    lineHeight: 1.6,
  },

  // Services
  servicesSection: {
    padding: '5rem 0',
    backgroundColor: '#ffffff',
  },
  servicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '2rem',
  },
  serviceCard: {
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '1rem',
    border: '1px solid #e2e8f0',
    transition: 'box-shadow 0.2s, transform 0.2s',
  },
  serviceIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  serviceCardTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: '0.75rem',
  },
  serviceCardText: {
    fontSize: '0.95rem',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: '1.25rem',
  },
  serviceList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  serviceListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
    color: '#334155',
  },

  // Ideal Clients
  idealClientsSection: {
    padding: '5rem 0',
    backgroundColor: '#f8fafc',
  },
  idealClientsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '2rem',
  },
  idealClientCard: {
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '1rem',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s',
  },
  idealClientTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#0f172a',
    margin: '1rem 0 0.5rem',
  },
  idealClientText: {
    fontSize: '0.9rem',
    color: '#64748b',
    lineHeight: 1.6,
  },
  idealClientsFooter: {
    textAlign: 'center',
    fontSize: '1rem',
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: '2rem',
  },

  // Integrations
  integrationsSection: {
    padding: '5rem 0',
    backgroundColor: '#ffffff',
  },
  integrationsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '3rem',
  },
  integrationsBlock: {
    backgroundColor: '#f8fafc',
    padding: '2rem',
    borderRadius: '1rem',
  },
  integrationsBlockTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: '1.5rem',
  },
  integrationsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  integrationTag: {
    backgroundColor: '#ffffff',
    color: '#475569',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    border: '1px solid #e2e8f0',
  },
  integrationsMention: {
    fontSize: '0.875rem',
    color: '#64748b',
    marginTop: '1rem',
    fontStyle: 'italic',
  },

  // Cost Estimator
  estimatorSection: {
    padding: '5rem 0',
    backgroundColor: '#f8fafc',
  },
  estimatorCard: {
    maxWidth: '700px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    padding: '2.5rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  estimatorForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  estimatorFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '1rem',
  },
  estimatorButton: {
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
  },
  estimatorResult: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    marginTop: '1.5rem',
    padding: '1.5rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '0.75rem',
    border: '1px solid #bbf7d0',
  },
  estimatorResultTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    color: '#065f46',
    marginBottom: '0.5rem',
  },
  estimatorResultText: {
    fontSize: '0.95rem',
    color: '#047857',
    lineHeight: 1.6,
    marginBottom: '0.75rem',
  },
  estimatorResultCta: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
  },

  // Lead Magnet
  leadMagnetSection: {
    padding: '5rem 0',
    backgroundColor: '#ffffff',
  },
  leadMagnetContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '3rem',
    backgroundColor: '#f8fafc',
    padding: '3rem',
    borderRadius: '1rem',
    border: '1px solid #e2e8f0',
  },
  leadMagnetLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  leadMagnetTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: '1rem',
  },
  leadMagnetSubtitle: {
    fontSize: '1rem',
    color: '#64748b',
    marginBottom: '2rem',
  },
  leadMagnetList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 2rem 0',
  },
  leadMagnetListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
    color: '#334155',
  },
  leadMagnetImage: {
    backgroundColor: '#e0f2fe',
    padding: '2rem',
    borderRadius: '0.75rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leadMagnetImageText: {
    marginTop: '1rem',
    fontSize: '0.875rem',
    color: '#3b82f6',
    fontWeight: '600',
  },
  leadMagnetRight: {
    display: 'flex',
    flexDirection: 'column',
  },
  leadMagnetForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },

  // FAQ
  faqSection: {
    padding: '5rem 0',
    backgroundColor: '#f8fafc',
  },
  faqGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    maxWidth: '800px',
    margin: '2rem auto 0',
  },
  faqItem: {
    backgroundColor: '#ffffff',
    borderRadius: '0.75rem',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  faqQuestionButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    gap: '1rem',
  },
  faqQuestion: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0,
  },
  faqAnswer: {
    fontSize: '0.95rem',
    color: '#475569',
    lineHeight: 1.7,
    padding: '0 1.5rem 1.25rem',
  },

  // Contact Section
  contactSection: {
    padding: '5rem 0',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
  },
  contactHeader: {
    textAlign: 'center',
    marginBottom: '3rem',
  },
  sectionTitleWhite: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '1rem',
  },
  sectionSubtitleWhite: {
    fontSize: '1.125rem',
    color: '#e0f2fe',
    maxWidth: '600px',
    margin: '0 auto',
  },
  contactContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '3rem',
  },
  contactForm: {
    backgroundColor: '#ffffff',
    padding: '2.5rem',
    borderRadius: '1rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  submitButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: '100%',
    transition: 'all 0.2s',
    marginTop: '0.5rem',
  },
  submitButtonLarge: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '1.125rem 2rem',
    borderRadius: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: '100%',
    transition: 'all 0.2s',
    marginTop: '0.5rem',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
  },
  formMessage: {
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  rgpdText: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '1rem',
    lineHeight: 1.5,
  },
  trustBadge: {
    fontSize: '0.75rem',
    color: '#10b981',
    fontWeight: '600',
    marginTop: '0.5rem',
    textAlign: 'center',
  },
  contactTrust: {
    marginTop: '1.5rem',
    fontSize: '0.875rem',
    color: '#475569',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  contactInfoTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '1rem',
  },
  contactInfoItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
  },
  contactInfoLabel: {
    fontSize: '0.875rem',
    color: '#e0f2fe',
    marginBottom: '0.25rem',
  },
  contactInfoValue: {
    fontSize: '1rem',
    color: '#ffffff',
    fontWeight: '500',
    textDecoration: 'none',
  },

  // Final CTA
  finalCtaSection: {
    padding: '5rem 0',
    backgroundColor: '#0f172a',
    textAlign: 'center',
  },
  finalCtaContent: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '0 20px',
  },
  finalCtaTitle: {
    fontSize: '2.25rem',
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: '1rem',
  },
  finalCtaSubtitle: {
    fontSize: '1.125rem',
    color: '#94a3b8',
    marginBottom: '2rem',
  },
  finalCtaButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '1.125rem 2.5rem',
    borderRadius: '0.5rem',
    fontSize: '1.2rem',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  },

  // Footer
  footer: {
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    padding: '3rem 0 1.5rem',
    borderTop: '1px solid #1e293b',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
  },
  footerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  footerColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  footerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  footerLogoText: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#ffffff',
  },
  footerDescription: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    lineHeight: 1.6,
  },
  footerColumnTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '0.5rem',
  },
  footerList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  footerLink: {
    color: '#94a3b8',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'color 0.2s',
  },
  footerContactItem: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
  },
  footerBottom: {
    borderTop: '1px solid #334155',
    paddingTop: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  footerCopyright: {
    fontSize: '0.875rem',
    color: '#64748b',
  },
  footerLinks: {
    display: 'flex',
    gap: '1.5rem',
  },
  footerLegalLink: {
    fontSize: '0.875rem',
    color: '#64748b',
    textDecoration: 'none',
  },
};

export default LandingPageGoogleAds;
